<template>
  <v-app :class="environmentBorderClass" :style="environmentColorStyle">
    <v-main>
      <vue-component-wrapper>
        <router-view />
      </vue-component-wrapper>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { useUiSettingsStore } from '@/stores/ui-settings.store'

const authStore = useAuthStore()
const uiSettingsStore = useUiSettingsStore()

const environmentBorderClass = computed(() => {
  if (!uiSettingsStore.isThemeApplied) return ''
  return 'env-border-active'
})

const environmentColorStyle = computed(() => {
  if (!uiSettingsStore.isThemeApplied) return {}

  const cssVar = uiSettingsStore.currentEnvColorCss

  // For hover states, we need a darker version
  const hoverColor = getHoverColor(uiSettingsStore.activeEnvColor)

  return {
    '--env-color': cssVar,
    '--env-color-hover': hoverColor
  }
})

const getHoverColor = (colorKey: string): string => {
  // Darker versions for hover states (700 shade)
  switch (colorKey) {
    case 'red':
      return '211, 47, 47' // red700
    case 'redOrange':
      return '230, 74, 25' // deepOrange700
    case 'orange':
      return '245, 124, 0' // orange700
    case 'amber':
      return '255, 160, 0' // amber700
    case 'yellow':
      return '251, 192, 45' // yellow700
    case 'lime':
      return '175, 180, 43' // lime700
    case 'green':
      return '56, 142, 60' // green700
    case 'teal':
      return '0, 121, 107' // teal700
    case 'blue':
      return '25, 118, 210' // blue700
    case 'indigo':
      return '48, 63, 159' // indigo700
    case 'purple':
      return '123, 31, 162' // purple700
    case 'redViolet':
      return '161, 18, 105' // redViolet darker
    default:
      return '56, 142, 60' // green700
  }
}

onMounted(() => {
  // Check if user is already logged in
  authStore.checkAuth()
})
</script>

<style lang="scss">
@use '@assets/scss/environment-theme.scss';
</style>
